import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { AnimatedWords } from "./HeroSection";
import { CARD_SUITS, FACE_CARDS } from "@/data/cards";
import { sutras } from "@/data/sutras";
import { useCopy } from "@/lib/copyVariant";
import { img } from "@/lib/img";

const EBOOK_URL = "https://customer-assets.emergentagent.com/job_c39cd2a4-adb8-4e70-b14e-b9bbd8f9d61f/artifacts/5hf4u04f_52%20Ideas%20to%20Stay%20Calm%20While%20Accomplishing%20More%20By%20Rajesh%20Setty%20%281%29.pdf";

const showcaseCards = [
  { ...CARD_SUITS.diamonds[0], sutra: sutras[0] },
  { ...CARD_SUITS.spades[2], sutra: sutras[2] },
  { ...FACE_CARDS[4], sutra: sutras[12] },
  { ...CARD_SUITS.hearts[1], sutra: sutras[1] },
];

// Apply per-variant sutra description overrides. Returns a fresh array so
// the underlying `sutras` import is never mutated.
function applySutraOverrides(cards, c) {
  let overrides = {};
  try { overrides = c("sutraOverrides") || {}; } catch (_) { /* noop */ }
  return cards.map((card) => {
    const override = overrides[card.sutra.id];
    if (!override) return card;
    return { ...card, sutra: { ...card.sutra, description: override } };
  });
}

const flyCards = [
  { src: CARD_SUITS.diamonds[0].url, from: "left", cls: "col-span-1" },
  { src: CARD_SUITS.hearts[4].url, from: "right", cls: "col-span-1" },
  { src: CARD_SUITS.spades[2].url, from: "bottom", cls: "col-span-1" },
  { src: FACE_CARDS[4].url, from: "left", cls: "col-span-1" },
  { src: CARD_SUITS.clubs[6].url, from: "right", cls: "col-span-1" },
  { src: FACE_CARDS[9].url, from: "bottom", cls: "col-span-1" },
];

function FlyingCard({ src, from, delay, className }) {
  const d = { left: { x: -180 }, right: { x: 180 }, bottom: { y: 120 } }[from] || { x: -180 };
  return (
    <motion.img src={src} alt="" aria-hidden="true" loading="lazy" decoding="async" width="240" height="336"
      initial={{ opacity: 0, rotate: from === "left" ? -12 : from === "right" ? 12 : 5, ...d }}
      whileInView={{ opacity: 1, x: 0, y: 0, rotate: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`rounded-lg sm:rounded-xl shadow-xl shadow-black/40 ${className}`} />
  );
}

// ─── Scroll phase helpers (shared across desktop + mobile) ───
const PHASE_END = 0.78;
function phaseFor(index, total) {
  const remainingPhase = PHASE_END - 0.01;
  const perCardPhase = remainingPhase / Math.max(1, total - 1);
  const enterStart = index === 0 ? 0 : 0.01 + (index - 1) * perCardPhase;
  const enterEnd = index === 0 ? 0.001 : enterStart + perCardPhase * 0.85;
  return { enterStart, enterEnd };
}

// ─── Desktop scroll-stacking card (horizontal layout, side panel + stacked cards) ───
function StackCardItem({ card, index, total, scrollYProgress }) {
  const { enterStart, enterEnd } = phaseFor(index, total);
  const yInputRange = index === 0 ? [0, 0.001] : [enterStart, enterEnd];
  const yOutputRange = index === 0 ? ["0vh", "0vh"] : ["110vh", "0vh"];
  const y = useTransform(scrollYProgress, yInputRange, yOutputRange);

  const remaining = total - index - 1;
  const finalScale = 1 - 0.05 * remaining;
  const scaleAfter = useTransform(scrollYProgress, [enterEnd, 1], [1, finalScale]);
  const offsetAfter = useTransform(scrollYProgress, [enterEnd, 1], [0, -28 * remaining]);

  return (
    <motion.div
      className="absolute inset-0 hidden lg:flex items-center justify-center"
      style={{ y, zIndex: index + 1 }}
    >
      <motion.div
        style={{ scale: scaleAfter, y: offsetAfter }}
        className="origin-center w-full max-w-[760px]"
      >
        <div className="relative bg-white rounded-3xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(70,70,70,0.45)] ring-1 ring-[#eee5db]">
          <div className="h-1.5" style={{ background: "linear-gradient(90deg, #96aeb4, #a6c1a8, #a89b94)" }} />
          <div className="grid grid-cols-[1fr_1.2fr] gap-8 p-10 items-center">
            <div className="flex justify-start">
              <img src={card.url} alt={card.name} loading="lazy" decoding="async" width="320" height="448"
                className="w-52 xl:w-60 rounded-2xl shadow-lg shadow-[#464646]/15" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.35em] text-[#7a9d83] font-semibold mb-2">
                {card.name} &middot; Sutra {card.sutra.id}
              </p>
              <h4 className="font-serif text-[28px] xl:text-[34px] text-[#464646] font-bold leading-[1.1] mb-3 tracking-tight">
                {card.sutra.title}
              </h4>
              <p className="text-base text-[#464646]/70 leading-relaxed">
                {card.sutra.description}
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Mobile/tablet stacked card: self-contained panel (image + text in one solid container).
//     Slides up from below and lands on top of previous cards. Previous cards shrink/shift up so
//     only a thin slice peeks above the new top card. Solid bg ensures the top card is fully readable.
function MobileStackCard({ card, index, total, scrollYProgress }) {
  const { enterStart, enterEnd } = phaseFor(index, total);

  const yInputRange = index === 0 ? [0, 0.001] : [enterStart, enterEnd];
  const yOutputRange = index === 0 ? ["0vh", "0vh"] : ["95vh", "0vh"];
  const y = useTransform(scrollYProgress, yInputRange, yOutputRange);

  // Once landed, shrink + push up slightly so the corner peeks above the new top card.
  // Lift kept small (-10px per remaining card) so peeks stay close to the active tile
  // and don't crowd the pinned heading above.
  const remaining = total - index - 1;
  const finalScale = 1 - 0.02 * remaining;
  const scaleAfter = useTransform(scrollYProgress, [enterEnd, 1], [1, finalScale]);
  const offsetAfter = useTransform(scrollYProgress, [enterEnd, 1], [0, -10 * remaining]);

  return (
    <motion.div
      className="absolute inset-0 lg:hidden flex items-start justify-center px-5 md:px-10 pt-[22vh] sm:pt-[24vh] pb-[10vh] sm:pb-[10vh]"
      style={{ y, zIndex: index + 1 }}
    >
      <motion.div
        style={{ scale: scaleAfter, y: offsetAfter }}
        className="origin-top w-full max-w-[290px] sm:max-w-[360px]"
      >
        {/* Tile - mirrors PuzzleSection image tile: rounded-2xl sm:rounded-3xl, sage ring,
            deep soft shadow. Sized to fit within the available sticky viewport. */}
        <div
          className="relative bg-white rounded-2xl sm:rounded-3xl overflow-hidden p-4 sm:p-5 shadow-[0_30px_80px_-30px_rgba(70,70,70,0.35)] ring-1 ring-[#a6c1a8]/30"
          data-testid={`mobile-stack-card-${index}`}
        >
          <img
            src={card.url}
            alt={card.name}
            loading="lazy"
            decoding="async"
            width="320"
            height="448"
            className="w-full block rounded-xl sm:rounded-2xl shadow-2xl shadow-[#464646]/20"
          />
          <div className="pt-3 sm:pt-4">
            <p className="text-[10px] sm:text-xs text-[#96aeb4] font-semibold uppercase tracking-[0.3em] mb-1.5 sm:mb-2">
              {card.name} &middot; Sutra {card.sutra.id}
            </p>
            <h3 className="font-serif text-lg sm:text-xl text-[#464646] font-bold leading-snug mb-1.5 sm:mb-2">
              {card.sutra.title}
            </h3>
            <p className="text-xs sm:text-sm text-[#464646]/70 leading-relaxed line-clamp-2">
              {card.sutra.description}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Mobile/tablet: pinned heading (top) + animated progress (bottom) ───
function MobileShowcaseChrome({ scrollYProgress, cards, c }) {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const handler = (p) => {
      let idx = 0;
      for (let i = cards.length - 1; i >= 0; i--) {
        const { enterStart, enterEnd } = phaseFor(i, cards.length);
        const midpoint = i === 0 ? 0 : (enterStart + enterEnd) / 2;
        if (p >= midpoint) { idx = i; break; }
      }
      setActiveIdx(idx);
    };
    return scrollYProgress.on("change", handler);
  }, [scrollYProgress, cards.length]);

  return (
    <>
      {/* TOP: Heading - mirrors the PuzzleSection "A hidden surprise / Not just a deck.
          A puzzle that grows a tree." pattern exactly: same eyebrow + 2-line serif H2 with
          italic sage accent on the second line. */}
      <div className="lg:hidden absolute top-0 left-0 right-0 z-50 px-5 md:px-10 pt-10 sm:pt-16 pointer-events-none">
        <div className="max-w-[520px] mx-auto text-center">
          <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.5em] text-[#96aeb4] font-semibold block mb-2 sm:mb-4">
            {c("showcaseEyebrow")}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#464646] leading-[1.05] tracking-tight"
              data-testid="mobile-showcase-heading">
            {c("showcaseHeadingLine1")}
            <br />
            <span className="italic text-[#7a9d83]">{c("showcaseHeadingLine2")}</span>
          </h2>
        </div>
      </div>

      {/* BOTTOM: animated progress dots + counter */}
      <div className="lg:hidden absolute bottom-0 left-0 right-0 z-50 pb-3 sm:pb-5 px-6 sm:px-10 pointer-events-none flex flex-col items-center gap-2"
           data-testid="mobile-showcase-progress">
        <div className="flex items-center gap-2">
          {cards.map((_, i) => (
            <motion.span
              key={i}
              animate={{
                width: i === activeIdx ? 28 : 8,
                backgroundColor: i === activeIdx ? "#7a9d83" : "rgba(70,70,70,0.18)",
              }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="h-2 rounded-full"
            />
          ))}
        </div>
        <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-[#464646]/55 font-bold">
          {String(activeIdx + 1).padStart(2, "0")} &nbsp;/&nbsp; {String(cards.length).padStart(2, "0")}
        </span>
      </div>
    </>
  );
}

function ProgressItem({ scrollYProgress, total, index, title }) {
  // Item highlights from the moment its card is the visible "top of stack" -
  // which we treat as the midpoint of the next card's entry animation (when
  // the next card has covered the previous one ~50%).
  const midpointBefore = (i) => {
    if (i === 0) return 0;
    const { enterStart, enterEnd } = phaseFor(i, total);
    return (enterStart + enterEnd) / 2;
  };
  const start = midpointBefore(index);
  const end = index === total - 1 ? 1 : midpointBefore(index + 1);

  const isFirst = start === 0;
  const isLast = end >= 0.999;
  const a = Math.max(0.0001, start - 0.02);
  const b = Math.min(0.9999, end + 0.02);
  // Build clean monotonic ranges (no duplicates) for framer-motion.
  let inputRange, outputRange;
  if (isFirst) {
    inputRange = [end, b];
    outputRange = [1, 0.45];
  } else if (isLast) {
    inputRange = [a, start];
    outputRange = [0.45, 1];
  } else {
    inputRange = [a, start, end, b];
    outputRange = [0.45, 1, 1, 0.45];
  }
  const opacity = useTransform(scrollYProgress, inputRange, outputRange);
  // Bigger active dot + bolder text via scale of the inner row
  const dotScale = useTransform(scrollYProgress, isFirst ? [end, b] : isLast ? [a, start] : [a, start, end, b],
    isFirst ? [1, 0.5] : isLast ? [0.5, 1] : [0.5, 1, 1, 0.5]);
  const textWeight = useTransform(scrollYProgress, isFirst ? [end, b] : isLast ? [a, start] : [a, start, end, b],
    isFirst ? [700, 500] : isLast ? [500, 700] : [500, 700, 700, 500]);
  return (
    <motion.div style={{ opacity }} className="flex items-center gap-4">
      <motion.span
        style={{ scale: dotScale }}
        className="block w-2.5 h-2.5 rounded-full bg-[#7a9d83] origin-center shadow-[0_0_0_4px_rgba(122,157,131,0.18)]"
      />
      <motion.span
        style={{ fontWeight: textWeight }}
        className="font-serif text-[19px] xl:text-[21px] text-[#464646] leading-tight"
      >
        {title}
      </motion.span>
    </motion.div>
  );
}

// ─── Side panel with live progress + active sutra title ───
function StackSidePanel({ scrollYProgress, cards, c }) {
  return (
    <div className="hidden lg:flex flex-col justify-center h-full pr-2 max-w-[380px]">
      <div>
        <span className="text-[11px] uppercase tracking-[0.5em] text-[#96aeb4] font-semibold block mb-4">
          {c("showcaseEyebrow")}
        </span>
        <h3 className="font-serif text-5xl xl:text-6xl font-bold text-[#464646] leading-[1.02] mb-6 tracking-tight">
          {c("showcaseHeadingLine1")}<br />{c("showcaseHeadingLine2")}
        </h3>
        <p className="text-base xl:text-lg text-[#464646]/70 leading-relaxed mb-10">
          {c("showcaseSideBody")}
        </p>
        <div className="space-y-4" data-testid="stack-progress">
          {cards.map((c, i) => (
            <ProgressItem
              key={i}
              scrollYProgress={scrollYProgress}
              total={cards.length}
              index={i}
              title={c.sutra.title}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ShowcaseSection() {
  const stackRef = useRef(null);
  const { scrollYProgress: stackProgress } = useScroll({
    target: stackRef,
    offset: ["start start", "end end"],
  });
  const { c } = useCopy();
  const variantCards = applySutraOverrides(showcaseCards, c);

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <>
      {/* ─── Text + Flying Cards ─── */}
      <section id="showcase" className="py-16 sm:py-24 md:py-36 px-5 md:px-10 overflow-hidden relative z-10 bg-[#fff8f3]" data-testid="about-deck-section">
        {/* Soft swirl pattern */}
        <img src={img("https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/co7ekdv0_2.png", { w: 1400 })}
          alt="" aria-hidden="true" loading="lazy" decoding="async" width="1400" height="900" className="absolute inset-0 w-full h-full object-cover opacity-[0.04] pointer-events-none" />
        {/* Decorative icons */}
        <img src={img("https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/1t86eduj_65cc832c1d107098d228ae29_Group%2026967.avif", { w: 160 })}
          alt="" aria-hidden="true" loading="lazy" decoding="async" width="80" height="80" className="absolute top-12 right-6 md:right-16 w-12 md:w-20 opacity-50 pointer-events-none animate-float" />
        <img src={img("https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/kvoym993_65cc832cacc7ac557a389a4c_orb.avif", { w: 128 })}
          alt="" aria-hidden="true" loading="lazy" decoding="async" width="64" height="64" className="absolute bottom-24 left-4 md:left-12 w-10 md:w-16 opacity-40 pointer-events-none animate-float" style={{animationDelay:'2s'}} />
        <div className="max-w-[1400px] mx-auto relative">
          <motion.span className="text-[10px] sm:text-[11px] uppercase tracking-[0.5em] text-[#96aeb4] font-semibold block mb-5"
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>{c("deckEyebrow")}</motion.span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[5rem] font-bold text-[#464646] leading-[1.1] tracking-tight mb-12 sm:mb-16 md:mb-20">
            <AnimatedWords text={c("deckHeading1")} />
            <br />
            <span className="text-gradient-sage">
              <AnimatedWords text={c("deckHeading2")} delay={0.25} />
            </span>
          </h2>

          {/* Flying cards - responsive grid (mobile + iPad portrait) / absolute (desktop lg+) */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4 lg:hidden mb-10 max-w-2xl mx-auto">
            {flyCards.map((c, i) => (
              <FlyingCard key={i} src={c.src} from={c.from} delay={i * 0.08} className="w-full" />
            ))}
          </div>
          <div className="hidden lg:block relative h-[480px] xl:h-[540px] mb-12">
            <FlyingCard src={flyCards[0].src} from="left" delay={0} className="absolute top-0 left-[4%] w-36 xl:w-44" />
            <FlyingCard src={flyCards[1].src} from="right" delay={0.1} className="absolute top-2 right-[10%] w-32 xl:w-40" />
            <FlyingCard src={flyCards[2].src} from="bottom" delay={0.2} className="absolute top-[32%] left-[26%] w-40 xl:w-52 z-10" />
            <FlyingCard src={flyCards[3].src} from="left" delay={0.3} className="absolute top-[28%] right-[16%] w-34 xl:w-44" />
            <FlyingCard src={flyCards[4].src} from="right" delay={0.4} className="absolute bottom-0 left-[12%] w-32 xl:w-40" />
            <FlyingCard src={flyCards[5].src} from="bottom" delay={0.48} className="absolute bottom-2 right-[5%] w-36 xl:w-46" />
          </div>

          {/* Description + suits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-start">
            <motion.p className="text-sm sm:text-base text-[#464646]/65 leading-relaxed"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              {c("deckBody")}
            </motion.p>
            <div className="grid grid-cols-4 gap-2 sm:gap-3">
              {[
                { s: "\u2666", n: "Diamonds", c: "#a89b94" },
                { s: "\u2665", n: "Hearts", c: "#96aeb4" },
                { s: "\u2660", n: "Spades", c: "#7a9d83" },
                { s: "\u2663", n: "Clubs", c: "#464646" },
              ].map((suit, i) => (
                <motion.div key={suit.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="text-center p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-white border border-[#464646]/8 hover:border-[#96aeb4]/40 hover:shadow-md hover:shadow-[#96aeb4]/10 transition-all">
                  <span className="text-2xl sm:text-3xl block" style={{ color: suit.c }}>{suit.s}</span>
                  <p className="text-[10px] sm:text-[11px] text-[#464646]/60 mt-1">{suit.n}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── True Scroll-Stacking Card Showcase ─── */}
      <section
        ref={stackRef}
        className="relative bg-[#f5ebe0]"
        style={{ height: `${(variantCards.length + 1) * 100}vh` }}
        data-testid="stack-showcase"
      >
        <div className="sticky top-0 h-screen overflow-hidden">
          {/* Mobile/Tablet chrome: pinned heading at top + animated progress at bottom */}
          <MobileShowcaseChrome scrollYProgress={stackProgress} cards={variantCards} c={c} />

          {/* Desktop 2-column: sidebar + stack canvas */}
          <div className="h-full hidden lg:grid px-14 xl:px-20 grid-cols-[400px_1fr] gap-16">
            <StackSidePanel scrollYProgress={stackProgress} cards={variantCards} c={c} />
            <div className="relative h-full">
              {variantCards.map((card, i) => (
                <StackCardItem
                  key={i}
                  card={card}
                  index={i}
                  total={variantCards.length}
                  scrollYProgress={stackProgress}
                />
              ))}
            </div>
          </div>

          {/* Mobile/Tablet canvas: vertical big-card layout */}
          <div className="lg:hidden relative h-full">
            {variantCards.map((card, i) => (
              <MobileStackCard
                key={i}
                card={card}
                index={i}
                total={variantCards.length}
                scrollYProgress={stackProgress}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ─── Mid-page CTA moved out: see NotJustADeckSection (rendered below SutrasSection) ─── */}
    </>
  );
}
